import React, { useMemo } from "react";
import { FaCircleCheck } from "react-icons/fa6";
import { usePricing } from "../hooks/usePricing.js";
import PlanCard from "./PlanCard.jsx";
import { PrimaryButton } from "./Buttons.jsx";
import Loader from "./Loader.jsx";
import { formatValue } from "../utils/Utils.js";
import Tooltip from "./Tooltip.jsx";

const PlanSelection = ({ 
  selectedPlan, 
  onPlanSelect, 
  storeCount = 1,
  showAddons = false,
  selectedAddons = [],
  onAddonToggle = () => {},
  mode = "select" // "select" or "display"
}) => {
  const { highestBasicPrice, highestGrowthPrice, tiers, calculatePrice, addonPrice } = usePricing();

  const estimatedPrice = useMemo(() => {
    if (!selectedPlan) return null;
    return calculatePrice(selectedPlan, storeCount);
  }, [selectedPlan, storeCount, calculatePrice]);

  const isSelected = (plan) => selectedPlan === plan;

  const seoBoostPrice = useMemo(() => {
    return addonPrice("seo-boost");
  }, [addonPrice]);
  
  const crmPrice = useMemo(() => {
    return addonPrice("crm");
  }, [addonPrice]);

  const isSEOIncluded = selectedPlan === "growth";

  return (
    <div className="text-sm">
      <div className="overflow-x-auto w-full flex justify-center items-center pt-4">
        <div className="md:min-w-max flex flex-col md:flex-row gap-8 md:gap-4">
          <PlanCard
            planType="basic"
            price={highestBasicPrice}
            tiers={tiers?.basic}
            tierPosition="right"
            selected={isSelected("basic")}
            onClick={() => mode === "select" && onPlanSelect("basic")}
            showSelectedBadge={mode === "select"}
          />

          <PlanCard
            planType="growth"
            price={highestGrowthPrice}
            tiers={tiers?.growth}
            tierPosition="left"
            selected={isSelected("growth")}
            onClick={() => mode === "select" && onPlanSelect("growth")}
            showSelectedBadge={mode === "select"}
          />
        </div>
      </div>
      
      {selectedPlan && estimatedPrice && (
        <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
              Monthly cost for {storeCount} location{storeCount !== 1 ? "s" : ""}
            </p>
            <p className="text-2xl font-bold text-slate-800 dark:text-white">
              ${estimatedPrice.toFixed(2)}/month
            </p>
          </div>
        </div>
      )}

      {showAddons && selectedPlan && (
        <div className="mt-8">
          <h2 className="text-lg font-bold text-center w-full my-4">
            Add-ons
          </h2>
          <div className="flex flex-col gap-4 items-center">
            {/* SEO Boost Add-on */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg py-2 px-4 w-full md:w-[700px] flex flex-col md:flex-row items-center">
              <div className="flex-1">
                <h3 className="font-bold text-lg my-2">SEO Boost Add-on</h3>
                <p className="text-sm">Enhance your visibility for customers to discover you online, driving more traffic and sales to your business.</p>
                <div className="flex gap-2 items-center my-4">
                  <FaCircleCheck className="text-green-600 text-lg flex-shrink-0" />
                  <span className="leading-tight">Optimize your website for organic traffic</span>
                </div>
                <div className="flex gap-2 items-center my-4">
                  <FaCircleCheck className="text-green-600 text-lg flex-shrink-0" />
                  <span className="leading-tight">Customize your website with advanced features</span>
                </div>
              </div>
              <div className="w-full md:w-[125px] ml-4 md:text-right">
                {isSEOIncluded ? (
                  <div className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                    Included in Growth plan
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-end gap-2 mb-2">
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={selectedAddons.includes("seo-boost")}
                        onChange={(e) => onAddonToggle("seo-boost", e.target.checked)}
                      />
                      <span className="text-sm">Select</span>
                    </div>
                    <div className="text-right mt-2">
                      <div className="text-2xl font-bold flex items-center justify-end">
                        {seoBoostPrice === "-" ? (
                          <Loader size={8} hideText />
                        ) : (
                          formatValue(seoBoostPrice / 100).replace(".00", "")
                        )}
                        <span className="text-sm font-normal">/mo</span>
                        <span className="inline-block relative left-1">
                          <Tooltip size="lg" position="left">
                            <span className="text-sm font-normal p-0">
                              Pay one price for all your locations
                            </span>
                          </Tooltip>
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* CRM Add-on */}
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg py-2 px-4 w-full md:w-[700px] flex flex-col md:flex-row items-center">
              <div className="flex-1">
                <h3 className="font-bold text-lg my-2">Customer Engagement</h3>
                <p className="text-sm">Enhance customer experience by managing relationships, tracking interactions, and improving marketing efforts with our efficient customer relationship management tools.</p>
                <div className="flex gap-2 items-center my-4">
                  <FaCircleCheck className="text-green-600 text-lg flex-shrink-0" />
                  <span className="leading-tight">Automated campaigns such as cart abandonment, weekly deals, re-engagement to improve customer retention</span>
                </div>
                <div className="flex gap-2 items-center my-4">
                  <FaCircleCheck className="text-green-600 text-lg flex-shrink-0" />
                  <span className="leading-tight">Custom email blasts</span>
                </div>
                <div className="flex gap-2 items-center my-4">
                  <FaCircleCheck className="text-green-600 text-lg flex-shrink-0" />
                  <span className="leading-tight">Upload customer list</span>
                </div>
              </div>
              <div className="w-full md:w-[125px] ml-4 md:text-right">
                <div className="flex items-center justify-end gap-2 mb-2">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={selectedAddons.includes("crm")}
                    onChange={(e) => onAddonToggle("crm", e.target.checked)}
                  />
                  <span className="text-sm">Select</span>
                </div>
                {crmPrice && Array.isArray(crmPrice) && crmPrice.length > 0 && (
                  <div className="text-right mt-2">
                    <div className="text-xs text-slate-600 dark:text-slate-400">Starts at</div>
                    <div className="text-2xl font-bold flex items-center justify-end">
                      {formatValue(crmPrice[0].flat_amount / 100).replace(".00", "")}
                      <span className="text-sm font-normal">/mo</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanSelection;
